<app-page-breadcrumb pageTitle="Limpieza de contactos de WhatsApp"></app-page-breadcrumb>

<div class="mb-4">
  <a
    routerLink="/whatsapp/setup"
    class="text-sm font-medium text-emerald-600 hover:underline dark:text-emerald-400"
  >
    ← Volver a la configuración de WhatsApp
  </a>
</div>

<div class="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
  <div class="flex flex-wrap items-start justify-between gap-4">
    <div class="min-w-0 flex-1">
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">
        Limpiar contactos creados por WhatsApp
      </h3>
      <p class="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
        Borra de una vez las fichas que se crearon solas mientras el alta automática estuvo
        encendida. Las conversaciones y los mensajes de WhatsApp
        <span class="font-medium text-gray-700 dark:text-gray-300">no se tocan</span>: los
        hilos siguen en la bandeja, identificados por el número.
      </p>
      <p class="mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
        Abajo tenés el detalle exacto de qué se borra y qué se conserva: es una
        previsualización, no cambia nada. Nada se elimina hasta que lo confirmes.
      </p>

      @if (!puedePrevisualizar) {
        <p class="mt-3 text-xs font-medium text-amber-600 dark:text-amber-400">
          Sólo un Admin o Super Admin puede usar esta herramienta.
        </p>
      } @else if (!puedeEjecutar) {
        <p class="mt-3 text-xs font-medium text-amber-600 dark:text-amber-400">
          Podés previsualizar, pero te falta el permiso de borrado para ejecutar la limpieza.
        </p>
      }
    </div>

    <button
      type="button"
      (click)="previsualizar()"
      [disabled]="!puedePrevisualizar || previsualizando || ejecutando"
      class="shrink-0 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
    >
      {{ previsualizando ? 'Calculando…' : (conteo ? 'Volver a calcular' : 'Previsualizar') }}
    </button>
  </div>

  @if (previsualizando && !conteo) {
    <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">
      Calculando qué se borraría…
    </p>
  }

  @if (errorMessage) {
    <div class="mt-4 flex items-start gap-3 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
      <span class="flex-1">{{ errorMessage }}</span>
      <button type="button" (click)="errorMessage = null" class="text-red-500 hover:text-red-700">×</button>
    </div>
  }

  @if (conteo && !hayAlgoQueLimpiar) {
    <!-- Nada que borrar: se dice con naturalidad y no se ofrece el botón de ejecutar. -->
    <div class="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-800/40">
      <p class="text-sm font-medium text-gray-900 dark:text-white">
        No hay contactos que limpiar.
      </p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Ninguna ficha del CRM viene del alta automática de WhatsApp. Se conservan
        {{ conteo.preservedConversations }} conversaciones y
        {{ conteo.preservedMessages }} mensajes.
      </p>
    </div>
  }

  @if (conteo && hayAlgoQueLimpiar) {
    <!-- Lo que se borra y lo que se conserva, con el mismo peso: la segunda columna es la
         que responde a la duda real («¿pierdo las conversaciones?»). -->
    <div class="mt-5 grid gap-4 md:grid-cols-2">
      <section class="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20">
        <p class="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
          Se elimina
        </p>
        <p class="mt-2 text-3xl font-bold text-red-700 dark:text-red-300">
          {{ conteo.contacts }}
        </p>
        <p class="text-sm text-red-700 dark:text-red-300">
          {{ conteo.contacts === 1 ? 'contacto creado por WhatsApp' : 'contactos creados por WhatsApp' }}
        </p>

        @if (totalEliminado > 0) {
          <p class="mt-4 text-xs text-red-700/80 dark:text-red-300/80">
            Se van con ellos {{ totalEliminado }}
            {{ totalEliminado === 1 ? 'registro que colgaba' : 'registros que colgaban' }}
            sólo de estos contactos:
          </p>
        } @else {
          <p class="mt-4 text-xs text-red-700/80 dark:text-red-300/80">
            No cuelga ningún otro registro de estos contactos: se borran sólo las fichas.
          </p>
        }
        <!-- El desglose se lista completo, también las categorías en cero (atenuadas): en un
             borrado irreversible, una fila que no aparece no dice si es cero o si nadie la
             miró. -->
        <ul class="mt-2 space-y-1">
          @for (fila of seEliminan; track fila.etiqueta) {
            <li
              class="flex items-baseline justify-between gap-3 text-sm text-red-800 dark:text-red-200"
              [class.opacity-50]="fila.cantidad === 0"
            >
              <span>{{ fila.etiqueta }}</span>
              <span class="font-semibold tabular-nums">{{ fila.cantidad }}</span>
            </li>
          }
        </ul>
      </section>

      <section class="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-900/20">
        <p class="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
          Se conserva
        </p>
        <div class="mt-2 flex flex-wrap gap-6">
          <div>
            <p class="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
              {{ conteo.preservedConversations }}
            </p>
            <p class="text-sm text-emerald-700 dark:text-emerald-300">
              {{ conteo.preservedConversations === 1 ? 'conversación' : 'conversaciones' }}
            </p>
          </div>
          <div>
            <p class="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
              {{ conteo.preservedMessages }}
            </p>
            <p class="text-sm text-emerald-700 dark:text-emerald-300">
              {{ conteo.preservedMessages === 1 ? 'mensaje' : 'mensajes' }}
            </p>
          </div>
        </div>
        <p class="mt-4 text-xs text-emerald-800/90 dark:text-emerald-200/90">
          No se borra ninguna conversación ni ningún mensaje: los hilos se quedan en la
          bandeja mostrando el número, y podés volver a crear la ficha de cualquiera de ellos
          desde el propio chat.
        </p>
        <p class="mt-2 text-xs text-emerald-800/90 dark:text-emerald-200/90">
          Reuniones, llamadas, correos, chats e historial de auditoría también se conservan:
          sólo pierden el vínculo con el contacto borrado.
        </p>
      </section>
    </div>

    <!-- Bloque aparte a propósito, y siempre visible: confundir «negocios que se borran»
         con «negocios que sólo pierden el vínculo» sería grave, y es justo la duda que trae
         al usuario a esta pantalla. -->
    <section class="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-900/20">
      <p class="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
        Se conserva, pero pierde el vínculo
      </p>
      @if (totalDesvinculado > 0) {
        <p class="mt-1 text-xs text-amber-800/90 dark:text-amber-200/90">
          Estos {{ totalDesvinculado }}
          {{ totalDesvinculado === 1 ? 'registro no se borra' : 'registros no se borran' }}
          —están compartidos con contactos que sobreviven—: sólo se les retira la referencia
          al contacto eliminado.
        </p>
      } @else {
        <p class="mt-1 text-xs text-amber-800/90 dark:text-amber-200/90">
          Ningún negocio ni tarea de estos contactos está compartido con un contacto que
          sobreviva. Si lo estuviera, el registro se conservaría y sólo perdería la
          referencia al contacto eliminado.
        </p>
      }
      <ul class="mt-2 space-y-1">
        @for (fila of seDesvinculan; track fila.etiqueta) {
          <li
            class="flex items-baseline justify-between gap-3 text-sm text-amber-900 dark:text-amber-200"
            [class.opacity-50]="fila.cantidad === 0"
          >
            <span>{{ fila.etiqueta }}</span>
            <span class="font-semibold tabular-nums">{{ fila.cantidad }}</span>
          </li>
        }
      </ul>
    </section>

    <p class="mt-5 text-sm text-gray-700 dark:text-gray-300">
      <span class="font-semibold text-red-700 dark:text-red-300">Esto no se puede deshacer.</span>
      Al ejecutarlo desaparecen las fichas y, con ellas, los negocios, leads, tickets, tareas
      y cotizaciones que colgaban sólo de esas fichas. No hay papelera ni forma de
      recuperarlos: la única marca que queda es el registro en el historial de auditoría.
    </p>

    <div class="mt-3 flex flex-wrap items-center gap-3">
      <button
        type="button"
        (click)="ejecutar()"
        [disabled]="!puedeEjecutar || ejecutando || previsualizando"
        class="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ ejecutando ? 'Eliminando…' : 'Eliminar ' + conteo.contacts + (conteo.contacts === 1 ? ' contacto' : ' contactos') }}
      </button>
      <span class="text-xs text-gray-500 dark:text-gray-400">
        Te vamos a pedir confirmación antes de borrar nada.
      </span>
    </div>
  }

  @if (ejecutadoEn) {
    <p class="mt-4 text-xs text-gray-500 dark:text-gray-400">
      Última limpieza ejecutada el {{ ejecutadoEn | date:'medium' }}.
    </p>
  }
</div>
